// @flow
import React, { useState, useEffect } from 'react';
import injectSheet from 'react-jss';
import { fetchSearch as __fetchSearch } from '~/roles/actions';
import TextInput from '~/app/components/TextInput';
import { A } from '~/app/ui/tokens';
import _ from 'shared/lib/gettext';
import { useDispatch, useSelector } from 'react-redux';
import { gql, useQuery } from '@apollo/client';

type Props = {
  classes: {
    [key: string]: string
  },
  onUpdate: Function
};

const DELAY_FETCHING = 1000;
const MINIMUM_CHAR = 3;

const LdapInput = (props: Props) => {
  const { classes: c } = props;

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [timerid, setTimerId] = useState('');
  const isEmptyResult = !results.length;

  // getUsers
  const { data } = useQuery(QUERY);

  // getRoles
  const { results: searchResults } = useSelector(mapStateToProps);

  const dispatch = useDispatch();
  const searchUsers = x => dispatch(__fetchSearch(x));

  // When user queries, the app will delay by 1 second before calling fetch.
  const onChangeTextInput = async (e) => {
    const { value } = e.target;

    setQuery(value);

    if (timerid) {
      clearTimeout(timerid);
    }

    const newTimerid = setTimeout(() => {
      searchUsername(value);
    }, DELAY_FETCHING);

    setTimerId(newTimerid);
  }

  const searchUsername = async (value) => {
    const { onUpdate } = props;

    if (value.length >= MINIMUM_CHAR) {
      // Results are return through props (Redux)
      await searchUsers(value);
    }

    onUpdate({
      username: '',
      email: ''
    });
  }

  // Update results based on Redux updates
  useEffect(() => {
    const roles = data?.users || [];
    const usernames = roles.map(o => o.username);

    let users = [];

    if (searchResults.length) {
      users = searchResults.filter(
        o => !usernames.includes(o.sAMAccountName)
      ).map(o => ({
        username: o.sAMAccountName,
        email: o.mail
      }));
    }

    setResults(users);
  }, [data, searchResults]);

  const onSelectUsername = (e) => {
    const { onUpdate } = props;
    const [username, email] = e.target.value.split(',');

    setQuery(username);
    setResults([]);

    onUpdate({ username, email });
  }

  return (
    <div className={c.relative}>
      <TextInput
        type="text"
        placeholder={_('SEARCH_HELP_TEXT')}
        value={query}
        additionalStyles={{ flexGrow: 1 }}
        onChange={onChangeTextInput}
      />
      {
        !isEmptyResult &&
        <div className={c.results}>
          <select
            multiple="multiple"
            className={c.selectResults}
            onChange={onSelectUsername}
          >
            {
              results.map(
                o => (
                  <option
                    key={o.username}
                    value={`${o.username},${o.email}`}
                  >
                    {o.username}
                  </option>
                )
              )
            }
          </select>
        </div>
      }
      {
        isEmptyResult &&
        <div>{_('NO_SEARCH_MATCH')}</div>
      }
    </div>
  );
}

/* =============================================================================
=    GraphQL: Get users
============================================================================= */
const QUERY = gql`
  query getUsers {
    users {
      _id
      username
      email
      createdBy
      createdAt
      updatedBy
      updatedAt
      isSuperAdmin
      isActive
      roles {
        categories {
          category
          roleType
        }
        others {
          category
          roleType
        }
      }
    }
  }
`;

/* =============================================================================
=    Redux
============================================================================= */
const mapStateToProps = state => ({
  results: state.roles.results
});

/* =============================================================================
=    CSS
============================================================================= */
const styles = {
  results: {
    position: 'absolute',
    zIndex: 1,
    background: A.surface,
    width: '100%'
  },
  relative: {
    position: 'relative',
    display: 'flex'
  },
  selectResults: {
    width: '100%',
    fontFamily: '"Montserrat", sans-serif',
    'overflow-y': 'auto'
  }
};

export default injectSheet(styles)(LdapInput);
